import cn from 'classnames';
import { ClientInfluencerAccount } from '../../helpers';
import useUserAnalytics from '../../useUserAnalytics';
import { Card, Skeleton, tableHelpers } from '@round/creatorbase-ui';
import { ColumnDef } from '@tanstack/react-table';
import { PhylloCreatorProfileBasicDetails } from '@round/api';
import { formatNumberToKNotation } from '@round/utils';
import { getTableMetaHelper, Image } from '@round/ui-kit';
import styles from './Lookalikes.module.css';
import VirtualizedTable from '../../components/VirtualizedTable/VirtualizedTable';
import TableEmptyState from '../../components/TableEmptyState/TableEmptyState';
import { assets } from '@round/whitelabel-ui';

const { Verified: VerifiedIcon } = assets;

type Meta = {
    isLoading: boolean;
    isError: boolean;
};

const getTableMeta = getTableMetaHelper<Meta>();

type Props = {
    account: ClientInfluencerAccount | undefined | null;
    isLoading?: boolean;
};

const Lookalikes = ({ account, isLoading }: Props) => {
    const {
        data,
        isLoading: isUserAnalyticsLoading,
        isError,
    } = useUserAnalytics(account, {
        enabled: !!account && !isLoading,
    });

    const lookalikes = data?.profile.lookalikes ?? [];

    const columns: ColumnDef<tableHelpers.WithLoadingRow<PhylloCreatorProfileBasicDetails>>[] = [
        {
            header: 'Account',
            id: 'account',
            cell: ({ row: { original }, table }) => {
                const { isLoading: isTableLoading } = getTableMeta(table);
                const isSkeletonRow = isTableLoading && tableHelpers.isLoadingRow(original);
                const username = original.platform_username ? `@${original.platform_username}` : '-';

                return (
                    <div className={styles.account}>
                        {isSkeletonRow ? (
                            <Skeleton className={cn(styles.accountImage, styles.accountImageSkeleton)} />
                        ) : (
                            <Image className={styles.accountImage} src={original.image_url ?? ''} alt={username} />
                        )}
                        <div className={styles.accountDetails}>
                            {isSkeletonRow ? (
                                <Skeleton className={styles.usernameSkeleton} />
                            ) : (
                                <a
                                    href={original.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.accountUsername}
                                >
                                    {username}
                                    {original.is_verified && <VerifiedIcon className={styles.verifiedIcon} />}
                                </a>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            header: 'Followers',
            id: 'followers',
            accessorFn: ({ follower_count }) => (follower_count ? formatNumberToKNotation(follower_count) : '-'),
            cell: tableHelpers.SkeletonCell,
        },
    ];

    const meta: Meta = {
        isLoading: isUserAnalyticsLoading || !!isLoading,
        isError: isError,
    };

    const noDataTitle = meta.isError ? 'Something went wrong' : 'No lookalikes found';
    const noDataDescription = meta.isError
        ? 'Failed to load lookalikes. Please try again.'
        : 'No lookalikes found for this creator. Please try again later.';

    return (
        <Card size="sm" variant="borderless" className={styles.cardContainer}>
            <Card.Header className={styles.cardHeader}>
                <Card.Title>Lookalikes</Card.Title>
            </Card.Header>
            <Card.Content className={styles.cardContent}>
                <VirtualizedTable
                    className={styles.table}
                    data={tableHelpers.getLoadingRows(lookalikes, { isLoading: meta.isLoading, pageSize: 25 })}
                    meta={meta}
                    columns={columns}
                    virtualizerOptions={{
                        count: lookalikes.length || 25,
                        estimateSize: () => 50,
                    }}
                    noDataLabel={
                        <TableEmptyState isError={meta.isError} title={noDataTitle} description={noDataDescription} />
                    }
                />
            </Card.Content>
        </Card>
    );
};

export default Lookalikes;
